/* import __COLOCATED_TEMPLATE__ from './workflow-side-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import SdrRoutingOutcomeEditorConfig from 'embercom/objects/visual-builder/configuration/editor/sdr-routing-outcome';
import type Store from '@ember-data/store';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

type WorkflowParams = Record<string, unknown> | undefined | null;

interface Signature {
  Args: {
    onClose: () => void;
    isOpen: boolean;
    routingOutcomeIdentifier: string;
    workflowParams?: WorkflowParams;
  };
}

export default class SalesDevelopmentWorkflowSideSheet extends Component<Signature> {
  @service declare store: Store;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked workflow?: Workflow;
  @tracked showConfirmationModal = false;
  @tracked private workflowArgsSignature?: string;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.initializeWorkflowFromArgs();
  }

  get editorConfiguration() {
    return new SdrRoutingOutcomeEditorConfig({
      workflow: this.workflow,
    });
  }

  get isWorkflowValid() {
    return this.workflow?.validations.isValid ?? false;
  }

  get isSaveDisabled() {
    return !this.isWorkflowValid;
  }

  get hasUnsavedChanges() {
    return !!this.workflow?.hasUnsavedChanges;
  }

  @action
  handleClose() {
    if (this.hasUnsavedChanges) {
      this.showConfirmationModal = true;
      return;
    }
    this.args.onClose();
  }

  @action
  confirmClose() {
    this.showConfirmationModal = false;
    if (this.workflow) {
      this.workflow.rollbackAttributes();
      this.args.onClose();
    } else {
      this.args.onClose();
    }
  }

  @action
  cancelClose() {
    this.showConfirmationModal = false;
  }

  @action
  saveWorkflow() {
    if (!this.workflow) {
      return;
    }

    let workflowPayload = this.serializeWorkflow();
    if (!workflowPayload) {
      return;
    }
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-sdr:workflow-updated',
      data: {
        routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
        workflowParams: workflowPayload,
      },
    });
    this.args.onClose();
  }

  @action
  deleteWorkflow() {
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-sdr:workflow-updated',
      data: {
        routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
        workflowParams: null,
      },
    });
    // Notify React to update CTA immediately for backwards compatibility
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-sdr:workflow-deleted',
      data: { routingOutcomeIdentifier: this.args.routingOutcomeIdentifier },
    });
    this.args.onClose();
  }

  @action
  handleWorkflowArgsChange() {
    this.initializeWorkflowFromArgs();
  }

  private initializeWorkflowFromArgs() {
    let signature = JSON.stringify({
      routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
      workflowParams: this.args.workflowParams,
    });

    if (signature === this.workflowArgsSignature) {
      return;
    }

    this.workflowArgsSignature = signature;

    if (this.workflow) {
      this.workflow.unloadRecord();
      this.workflow = undefined;
    }

    if (this.args.workflowParams) {
      this.workflow = this.pushWorkflowPayload(this.args.workflowParams);
    } else {
      this.workflow = this.createEmptyWorkflowRecord();
    }
  }

  private pushWorkflowPayload(payload: Record<string, unknown>) {
    this.store.pushPayload({ 'operator/visual-builder/workflow': payload });

    return this.store.peekRecord(
      'operator/visual-builder/workflow',
      payload.id as string,
    ) as Workflow;
  }

  private createEmptyWorkflowRecord() {
    let workflow = this.store.createRecord('operator/visual-builder/workflow');
    let group = this.store.createRecord('operator/visual-builder/group', {
      isStart: true,
      title: null,
    });

    group.steps.clear();
    workflow.groups.pushObject(group);

    return workflow;
  }

  private serializeWorkflow() {
    if (!this.workflow) {
      return null;
    }

    return this.workflow.serialize();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::SalesDevelopment::WorkflowSideSheet': typeof SalesDevelopmentWorkflowSideSheet;
    'ai-agent/sales-development/workflow-side-sheet': typeof SalesDevelopmentWorkflowSideSheet;
  }
}
